
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export class HotLaunchError extends Error {
    public code: number;
    public txId?: string;

    constructor(message: string, code: number, txId?: string) {
        super(message);
        this.name = 'HotLaunchError';
        this.code = code;
        this.txId = txId;
    }
}

export enum HotLaunchErrorCode {
    // Generic
    UNKNOWN = 0,
    UNAUTHORIZED = 401,
    INSUFFICIENT_FUNDS = 402,
    
    // Minting
    MINT_EXISTS = 6000,
    INVALID_METADATA = 6001,
    EXTENSION_ERROR = 6002,
    
    // Trading
    SLIPPAGE_EXCEEDED = 6010,
    LIQUIDITY_INSUFFICIENT = 6011,
    CURVE_NOT_FOUND = 6012,
    BOT_DETECTED = 6013,
    
    // Liquidity
    MARKET_ALREADY_EXISTS = 6020,
    LOCK_PERIOD_INVALID = 6021
}

export const ERROR_MESSAGES: Record<number, string> = {
    [HotLaunchErrorCode.UNKNOWN]: "An unknown error occurred on-chain.",
    [HotLaunchErrorCode.UNAUTHORIZED]: "Wallet is not authorized to perform this action.",
    [HotLaunchErrorCode.INSUFFICIENT_FUNDS]: "Insufficient SOL/Tokens to complete transaction.",
    [HotLaunchErrorCode.MINT_EXISTS]: "This mint address has already been initialized.",
    [HotLaunchErrorCode.INVALID_METADATA]: "Metadata URI provided is invalid or unreachable.",
    [HotLaunchErrorCode.EXTENSION_ERROR]: "Failed to initialize Token-2022 extensions.",
    [HotLaunchErrorCode.SLIPPAGE_EXCEEDED]: "Price moved beyond your slippage tolerance.",
    [HotLaunchErrorCode.LIQUIDITY_INSUFFICIENT]: "Not enough liquidity in the bonding curve for this trade size.",
    [HotLaunchErrorCode.CURVE_NOT_FOUND]: "Bonding curve state not initialized for this token.",
    [HotLaunchErrorCode.BOT_DETECTED]: "Transaction blocked by anti-snipe protection.",
    [HotLaunchErrorCode.MARKET_ALREADY_EXISTS]: "OpenBook market ID already created for this pair.",
    [HotLaunchErrorCode.LOCK_PERIOD_INVALID]: "Lock duration must be at least 1 day."
};

/**
 * Parses raw Solana simulation logs or error objects into a typed HotLaunchError
 */
export const parseSolanaError = (error: any, txId?: string): HotLaunchError => {
    // 1. Check for custom program error codes in logs
    if (error?.logs && Array.isArray(error.logs)) {
        const customErrorLog = error.logs.find((log: string) => log.includes('Custom program error:'));
        if (customErrorLog) {
            const hexCode = customErrorLog.split('Custom program error: ')[1];
            const code = parseInt(hexCode, 16);
            const msg = ERROR_MESSAGES[code] || "Unknown Program Error";
            return new HotLaunchError(msg, code, txId);
        }
    }

    // 2. Check for string matches in message
    const msg = error?.message || error?.toString() || "";
    
    if (msg.includes("0x1")) return new HotLaunchError(ERROR_MESSAGES[HotLaunchErrorCode.INSUFFICIENT_FUNDS], HotLaunchErrorCode.INSUFFICIENT_FUNDS, txId);
    if (msg.includes("Slippage tolerance exceeded")) return new HotLaunchError(ERROR_MESSAGES[HotLaunchErrorCode.SLIPPAGE_EXCEEDED], HotLaunchErrorCode.SLIPPAGE_EXCEEDED, txId);

    // 3. Fallback
    return new HotLaunchError(msg, HotLaunchErrorCode.UNKNOWN, txId);
};
